import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { commissionService } from '../services/commission.service';
import {
  Plus,
  Edit,
  Trash2,
  Users,
  AlertCircle,
  CheckCircle,
  RefreshCw,
  Search,
  X,
  ArrowLeft,
} from 'lucide-react';
import ConfirmModal from '../components/ConfirmModal';

const CommissionManagement = () => {
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const [commissions, setCommissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingCommission, setEditingCommission] = useState(null);
  const [commissionToDelete, setCommissionToDelete] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    active: true,
  });

  useEffect(() => {
    if (user && isAdmin()) {
      loadCommissions();
    }
  }, [user, isAdmin]);

  const loadCommissions = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await commissionService.getCommissions();
      setCommissions(response);
    } catch (err) {
      console.error('Erreur lors du chargement des commissions:', err);
      setError('Impossible de charger la liste des commissions.');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      loadCommissions();
      return;
    }

    try {
      setLoading(true);
      const results = await commissionService.searchCommissions(searchQuery);
      setCommissions(results);
    } catch (err) {
      console.error('Erreur lors de la recherche:', err);
      setError('Erreur lors de la recherche.');
    } finally {
      setLoading(false);
    }
  };

  const openModal = (commission = null) => {
    if (commission) {
      setEditingCommission(commission);
      setFormData({
        name: commission.name,
        description: commission.description || '',
        active: commission.isActive !== undefined ? commission.isActive : commission.active,
      });
    } else {
      setEditingCommission(null);
      setFormData({
        name: '',
        description: '',
        active: true,
      });
    }
    setShowModal(true);
    setError('');
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingCommission(null);
    setFormData({
      name: '',
      description: '',
      active: true,
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!formData.name.trim()) {
      setError('Le nom de la commission est requis');
      return;
    }

    try {
      setActionLoading('submit');
      if (editingCommission) {
        await commissionService.updateCommission(editingCommission.id, formData);
        setSuccess('Commission modifiée avec succès !');
      } else {
        await commissionService.createCommission(formData);
        setSuccess('Commission créée avec succès !');
      }
      closeModal();
      await loadCommissions();
    } catch (err) {
      console.error('Erreur lors de la sauvegarde:', err);
      setError(err.message || 'Une erreur est survenue lors de la sauvegarde.');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteClick = (commission) => {
    setCommissionToDelete(commission);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!commissionToDelete) return;

    try {
      setActionLoading(commissionToDelete.id);
      setError('');
      setSuccess('');
      await commissionService.deleteCommission(commissionToDelete.id);
      setSuccess('Commission supprimée avec succès !');
      await loadCommissions();
      setShowDeleteModal(false);
      setCommissionToDelete(null);
    } catch (err) {
      console.error('Erreur lors de la suppression:', err);
      setError(err.message || 'Impossible de supprimer la commission.');
    } finally {
      setActionLoading(null);
    }
  };

  if (!isAdmin()) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center border border-gray-200">
          <div className="w-16 h-16 bg-red-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-10 h-10 text-red-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Accès refusé</h2>
          <p className="text-gray-600">
            Vous n'avez pas les permissions nécessaires pour accéder à cette page.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      <div className="max-w-6xl mx-auto">
        {/* Bouton retour */}
        <div className="mb-4">
          <button
            onClick={() => navigate('/admin')}
            className="inline-flex items-center gap-2 text-primary-600 hover:text-primary-700 font-medium transition-colors group"
          >
            <ArrowLeft size={20} className="group-hover:-translate-x-1 transition-transform" />
            <span>Retour à l'administration</span>
          </button>
        </div>

        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
              Gestion des Commissions
            </h1>
            <button
              onClick={() => openModal()}
              className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              <Plus className="w-5 h-5" />
              <span className="hidden sm:inline">Nouvelle Commission</span>
            </button>
          </div>
          <p className="text-gray-600">Gérez les commissions du dahira</p>
        </div>

        {/* Barre de recherche */}
        <div className="mb-6">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                placeholder="Rechercher une commission..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <button
              onClick={handleSearch}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              Rechercher
            </button>
            {searchQuery && (
              <button
                onClick={() => {
                  setSearchQuery('');
                  loadCommissions();
                }}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>

        {/* Messages */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        {success && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-green-800">{success}</p>
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          </div>
        )}

        {/* Liste des commissions */}
        {!loading && commissions.length === 0 && (
          <div className="bg-white rounded-xl shadow-sm p-12 text-center border border-gray-200">
            <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Users className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Aucune commission</h3>
            <p className="text-gray-600 mb-4">Commencez par créer votre première commission.</p>
            <button
              onClick={() => openModal()}
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              <Plus className="w-5 h-5" />
              Créer une commission
            </button>
          </div>
        )}

        {!loading && commissions.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {commissions.map((commission) => (
              <div
                key={commission.id}
                className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">
                      {commission.name}
                    </h3>
                    <span
                      className={`inline-block px-2 py-1 text-xs font-medium rounded-full ${
                        (commission.isActive !== undefined ? commission.isActive : commission.active)
                          ? 'bg-green-100 text-green-700'
                          : 'bg-gray-100 text-gray-700'
                      }`}
                    >
                      {(commission.isActive !== undefined ? commission.isActive : commission.active) ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>

                {commission.description && (
                  <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                    {commission.description}
                  </p>
                )}

                <div className="flex items-center gap-2 text-sm text-gray-500 mb-4">
                  <Users className="w-4 h-4" />
                  <span>{commission.memberCount || 0} membre(s)</span>
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={() => openModal(commission)}
                    disabled={actionLoading === commission.id}
                    className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors text-sm font-medium"
                  >
                    <Edit className="w-4 h-4" />
                    Modifier
                  </button>
                  <button
                    onClick={() => handleDeleteClick(commission)}
                    disabled={actionLoading === commission.id}
                    className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors text-sm font-medium"
                  >
                    {actionLoading === commission.id ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4" />
                    )}
                    Supprimer
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Modal Créer/Modifier */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                {editingCommission ? 'Modifier la commission' : 'Nouvelle commission'}
              </h2>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nom de la commission *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Ex: Commission Culturelle"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows="3"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Description de la commission..."
                  />
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="active"
                    checked={formData.active}
                    onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <label htmlFor="active" className="ml-2 block text-sm text-gray-700">
                    Commission active
                  </label>
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    disabled={actionLoading === 'submit'}
                    className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium disabled:opacity-50"
                  >
                    {actionLoading === 'submit' ? (
                      <RefreshCw className="w-5 h-5 animate-spin mx-auto" />
                    ) : editingCommission ? (
                      'Modifier'
                    ) : (
                      'Créer'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Modal de confirmation de suppression */}
        <ConfirmModal
          isOpen={showDeleteModal}
          onClose={() => {
            setShowDeleteModal(false);
            setCommissionToDelete(null);
          }}
          onConfirm={handleConfirmDelete}
          title="Supprimer la commission"
          message={
            commissionToDelete
              ? `Voulez-vous vraiment supprimer la commission "${commissionToDelete.name}" ? Cette action est irréversible.`
              : ''
          }
          confirmText="Supprimer"
          cancelText="Annuler"
          variant="danger"
          loading={actionLoading === commissionToDelete?.id}
        />
      </div>
    </div>
  );
};

export default CommissionManagement;
